import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';

import {
    Typography, LinearProgress, IconButton,
} from '@material-ui/core';

import TrackedLink from './TrackedLink';
import { activityLabels } from '../constants';
import {
    PrintIcon, ListIcon,
} from '../icons';
import { hasValue } from '../../youwell-common/objectUtils';
import apiUrls from '../../youwell-common/apiUrls';
import Favourite from './Favourite';

const styles = theme => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    links: {
        display: 'flex',
        flexWrap: 'wrap',
        flexGrow: 1,
        alignItems: 'center',
        [theme.breakpoints.up('sm')]: {
            marginBottom: theme.spacing(1),
        },
    },
    linkContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    linkItem: {
        fontWeight: theme.typography.fontWeightMedium,
    },
    separatorIcon: {
        margin: theme.spacing(0, 0.5),
        color: theme.palette.text.secondary,
    },
    flex: {
        flex: '1 1 auto',
    },
    progressContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flexGrow: 2,
        alignItems: 'center',
        justifyContent: 'space-between',

        '@media print': {
            display: 'none',
        },
    },
    progressBar: {
        height: 12,
        maxWidth: 200,
        minWidth: 120,
        flexGrow: 1,
    },
    progressBarRounded: {
        background: theme.palette.primary.main,
        borderRadius: theme.youwell.shapes.borderRadius,
        border: '2px solid',
    },
    progressBarFill: {
        background: theme.youwell.colors.highlight,
        borderRadius: theme.youwell.shapes.borderRadius,
    },
    progressText: {
        fontWeight: theme.typography.fontWeightMedium,
        marginLeft: theme.spacing(1),
    },
    actionsContainer: {
        display: 'flex',
        flexGrow: 1,
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    action: {
        padding: theme.spacing(0.5),
        marginLeft: theme.spacing(1),
    },
    actionIcon: {
        fontSize: 'inherit',
    },
    hideOnDesktop: {
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
});

class BreadCrumb extends React.Component {
    renderActions() {
        const {
            classes, task, openSideMenu, translate,
        } = this.props;

        return (
            <>
                {task?.printFileId && (
                    <IconButton className={classes.action} component="a" href={`${apiUrls.baseUrl}/${apiUrls.media.file}/${task.printFileId}`} target="_blank" rel="noopener noreferrer">
                        <PrintIcon className={classes.actionIcon} />
                    </IconButton>
                )}
                {task?.showFavourite && (
                    <Favourite className={classes.action} taskId={task.id} patientProgramElementId={task.patientProgramElementId} />
                )}
                {openSideMenu && (
                    <IconButton className={classnames(classes.action, classes.hideOnDesktop)} onClick={openSideMenu} title={translate('openMenu')}>
                        <ListIcon className={classes.actionIcon} />
                    </IconButton>
                )}
            </>
        );
    }

    render() {
        const {
            classes, translate, levels, progress, className, isActiveProgram,
        } = this.props;

        const showProgress = isActiveProgram && hasValue(progress);

        return (
            <div className={classnames(classes.root, className)}>
                <div className={classes.links}>
                    {levels && levels.map((l, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={`${i}${l.url || l.text}`} className={classes.linkContainer}>
                            <TrackedLink to={l.url} label={`${activityLabels.breadcrumb.link}/${i + 1}`}>
                                <Typography variant="caption" color={i < (levels.length - 1) ? 'textSecondary' : 'initial'} className={classes.linkItem}>
                                    {l.text}
                                </Typography>
                            </TrackedLink>

                            {i < (levels.length - 1) && (
                                <span className={classes.separatorIcon} aria-hidden="true">/</span>
                            )}
                        </div>
                    ))}
                    {!showProgress && (
                        <>
                            <div className={classes.flex} />
                            <div className={classes.linkContainer}>
                                {this.renderActions()}
                            </div>
                        </>
                    )}
                </div>


                <div className={classes.progressContainer}>
                    {showProgress && (
                        <>
                            <LinearProgress variant="determinate" value={progress * 100} className={classes.progressBar} classes={{ root: classes.progressBarRounded, bar: classes.progressBarFill }} />
                            <Typography variant="caption" className={classes.progressText} align="center" display="block">
                                {translate('common.progressCompleted', { progress: Math.round(progress * 100) })}
                            </Typography>
                            <div className={classes.actionsContainer}>
                                {this.renderActions()}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

BreadCrumb.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    levels: PropTypes.array,
    progress: PropTypes.number,
    className: PropTypes.string,
    task: PropTypes.object,
    openSideMenu: PropTypes.func,
    isActiveProgram: PropTypes.bool,
};

BreadCrumb.defaultProps = {
    levels: null,
    progress: null,
    className: null,
    task: null,
    openSideMenu: null,
    isActiveProgram: false,
};


const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    clientConfig: state.auth.clientConfig,
    isActiveProgram: state.programStore.currentProgram?.isActiveProgram,
});

export default connect(mapStateToProps)(withStyles(styles)(BreadCrumb));
